/**
 * Who is in which foursome, edited by hand.
 *
 * Setup could draw a whole week and reorder its tee times, and could not move
 * one man one group. The two levers it had were "regenerate everything" —
 * which mints new group ids, and a signature keys on roundId~groupId, so it
 * un-settles every card that has been signed — and nothing at all. That is
 * unusable on the morning somebody's flight is canceled, and it is unusable
 * on the evening the group decides Thursday should be drawn differently.
 *
 * So the rule every function here obeys is: change `playerIds`, and nothing
 * else about a group. The id, the tee time, the slot and the flight stamp all
 * ride through untouched, because the id is what the money is hanging off.
 *
 * The second rule is that no edit may leave the round quietly saying something
 * untrue. A contest names two men inside a foursome; move one of them out and
 * that contest is a fixture between a man who is there and a man who is not,
 * which the scoring engine will happily tabulate. So an edit CLEARS the
 * contests of every group it touched, rather than rewriting them: the app does
 * not know who these four want to play, and inventing an answer that looks
 * authoritative is the one thing this codebase exists not to do. What replaces
 * the guess is `seatingProblems`, which says out loud that the round cannot be
 * scored, and `redrawContests`, which pairs them off again on request — in
 * place, with the ids intact, which is the whole difference between this and
 * pressing "Generate pairings".
 */

import type {
  FormatKind,
  Group,
  GroupId,
  Match,
  Pairing,
  PlayerId,
  RoundId,
  Tournament,
} from './types'
import { clockTime } from './clock'

/**
 * The formats that are played as head-to-head contests inside the foursome,
 * and therefore score nothing at all without pairings. Quota and the
 * championship rank whoever is in front of them and need none.
 */
export const needsContests = (kind: FormatKind): boolean =>
  kind === 'individualMatchPlay' ||
  kind === 'teamHighLow' ||
  kind === 'teamCombinedNet' ||
  kind === 'teamFourBall'

/**
 * What the round is played for, in the words the group uses.
 *
 * Deliberately not the preset labels from `data/seed`: those are the titles on
 * a picker, and these have to read inside a sentence about what has gone wrong.
 */
const CONTEST_WORDS: Record<string, string> = {
  individualMatchPlay: 'singles match play',
  teamHighLow: 'high/low',
  teamCombinedNet: 'combined net',
  teamFourBall: 'four-ball',
}

/** The group a man is down to play in for a round, if he is down for one. */
export const groupOf = (
  t: Tournament,
  roundId: RoundId,
  playerId: PlayerId,
): Group | undefined =>
  t.groups.find((g) => g.roundId === roundId && g.playerIds.includes(playerId))

/** Men on the active roster with no seat in this round. Empty until it is drawn. */
export function unseated(t: Tournament, roundId: RoundId): PlayerId[] {
  const inRound = t.groups.filter((g) => g.roundId === roundId)
  if (inRound.length === 0) return []
  const seated = new Set(inRound.flatMap((g) => g.playerIds))
  return t.players.filter((p) => p.active && !seated.has(p.id)).map((p) => p.id)
}

/**
 * Whether a group's contests still describe the men in it.
 *
 * Both directions matter and only one of them is obvious. A contest naming
 * somebody who has left scores a match against a man who is not there; a man
 * in the group named by no contest simply plays for nothing, and neither the
 * card nor the leaderboard says so.
 */
function contestsAreStale(t: Tournament, match: Match, g: Group): boolean {
  const mine = t.pairings.filter((p) => p.groupId === g.id && p.matchId === match.id)
  if (mine.length === 0) return true
  const named = mine.flatMap((p) => p.sides.flatMap((s) => s.playerIds))
  return (
    named.some((id) => !g.playerIds.includes(id)) ||
    g.playerIds.some((id) => !named.includes(id))
  )
}

/** Everything an edit throws away in the groups it touched. */
const withoutContests = (t: Tournament, touched: Set<GroupId>): Pairing[] =>
  t.pairings.filter((p) => !touched.has(p.groupId))

/**
 * Put a man in a foursome, taking him out of whichever one he was in.
 *
 * The removal is not a courtesy — it is the enforcement of the one rule that
 * cannot bend, which is that a man plays in exactly one group per round. Doing
 * it by filtering every OTHER group in the round rather than the one he was
 * found in also repairs a document that already had him in two.
 */
export function seatIn(
  t: Tournament,
  roundId: RoundId,
  playerId: PlayerId,
  groupId: GroupId,
): Tournament {
  const to = t.groups.find((g) => g.id === groupId && g.roundId === roundId)
  if (!to || to.playerIds.includes(playerId)) return t

  const touched = new Set<GroupId>([to.id])
  for (const g of t.groups) {
    if (g.roundId === roundId && g.id !== to.id && g.playerIds.includes(playerId)) {
      touched.add(g.id)
    }
  }

  return {
    ...t,
    groups: t.groups.map((g) => {
      if (!touched.has(g.id)) return g
      return g.id === to.id
        ? { ...g, playerIds: [...g.playerIds, playerId] }
        : { ...g, playerIds: g.playerIds.filter((id) => id !== playerId) }
    }),
    pairings: withoutContests(t, touched),
  }
}

/**
 * Take a man out of the round's draw without putting him anywhere.
 *
 * A threesome is a real state — somebody is ill, or driving to the airport —
 * and it has to be reachable, because the alternative is that the only way to
 * remove a man from Thursday is to remove him from the roster, which clears
 * every draw he appears in.
 */
export function seatOut(t: Tournament, roundId: RoundId, playerId: PlayerId): Tournament {
  const touched = new Set(
    t.groups
      .filter((g) => g.roundId === roundId && g.playerIds.includes(playerId))
      .map((g) => g.id),
  )
  if (touched.size === 0) return t
  return {
    ...t,
    groups: t.groups.map((g) =>
      touched.has(g.id) ? { ...g, playerIds: g.playerIds.filter((id) => id !== playerId) } : g,
    ),
    pairings: withoutContests(t, touched),
  }
}

/**
 * Trade two men between their foursomes.
 *
 * The move a group actually asks for. Expressed as its own operation rather
 * than as two calls to `seatIn` because the sizes never wobble — two foursomes
 * stay two foursomes, where the pair of moves passes through a fivesome and a
 * threesome — and because each man lands in the seat the other left, which
 * keeps the printed order of the group recognisable to whoever is reading it.
 */
export function swapSeats(
  t: Tournament,
  roundId: RoundId,
  a: PlayerId,
  b: PlayerId,
): Tournament {
  const ga = groupOf(t, roundId, a)
  const gb = groupOf(t, roundId, b)
  if (!ga || !gb || ga.id === gb.id) return t
  const touched = new Set([ga.id, gb.id])
  const put = (ids: PlayerId[], out: PlayerId, into: PlayerId): PlayerId[] =>
    ids.map((id) => (id === out ? into : id))
  return {
    ...t,
    groups: t.groups.map((g) =>
      g.id === ga.id
        ? { ...g, playerIds: put(g.playerIds, a, b) }
        : g.id === gb.id
          ? { ...g, playerIds: put(g.playerIds, b, a) }
          : g,
    ),
    pairings: withoutContests(t, touched),
  }
}

/**
 * Everything wrong with this round's seating, in sentences.
 *
 * Derived on every read rather than recorded by whatever made the mess, so it
 * is equally true of a hand edit, an old document and another phone's save.
 * This is what stops a format that needs pairings from becoming silently
 * unscoreable: the round cannot be quietly broken, only loudly broken.
 */
export function seatingProblems(t: Tournament, roundId: RoundId): string[] {
  const round = t.rounds.find((r) => r.id === roundId)
  const inRound = t.groups.filter((g) => g.roundId === roundId)
  // A round with no draw is not a round with a problem — "no draw" is already
  // said once, by `startProblems`, and saying it twice in different words
  // reads as two faults.
  if (!round || inRound.length === 0) return []

  const out: string[] = []
  const name = (id: PlayerId): string =>
    t.players.find((p) => p.id === id)?.displayName ?? id

  for (const g of inRound) {
    for (const id of g.playerIds) {
      const seats = inRound.filter((x) => x.playerIds.includes(id))
      if (seats.length > 1 && seats[0] === g) {
        out.push(
          `${name(id)} is down in ${seats.length} groups — he can only be in one of them.`,
        )
      }
    }
  }

  const match = t.matches.find((m) => m.roundId === roundId)
  /*
   * A round somebody added on a tee has a partial field ON PURPOSE — eight men
   * at ten past four — so the other four are not missing, they are at dinner.
   * `counts` is the marker, because the server writes it explicitly on every
   * round created from a phone and no scheduled round has ever carried it.
   */
  if (match?.counts === undefined) {
    for (const id of unseated(t, roundId)) {
      out.push(`${name(id)} is in no group — give him a foursome or the round has no card for him.`)
    }
  }

  if (match && needsContests(match.config.kind)) {
    const word = CONTEST_WORDS[match.config.kind] ?? 'a head-to-head contest'
    for (const g of inRound) {
      if (!contestsAreStale(t, match, g)) continue
      out.push(
        `The ${clockTime(g.teeTime)} group is not paired off — this round is ${word}, so it will score nothing for those ${g.playerIds.length} until they are.`,
      )
    }
  }

  return out
}

/**
 * How a foursome splits into contests: sort on strokes, then low with high
 * against the two in the middle, and the same pairing off for singles.
 *
 * One copy, because there were two. `AddRound` builds the contests for a round
 * created on a tee and this module rebuilds them after an edit, and they had
 * each written the split out — two statements of one rule, free to drift into
 * disagreeing about what a level 2v2 is. Returns a list of contests, each a
 * pair of sides.
 */
export function levelSplit(
  playerIds: readonly PlayerId[],
  kind: FormatKind,
  strokesOf: (playerId: PlayerId) => number,
): PlayerId[][][] {
  const byStrokes = [...playerIds].sort((a, b) => strokesOf(a) - strokesOf(b))
  if (kind === 'individualMatchPlay') {
    const out: PlayerId[][][] = []
    for (let i = 0; i < byStrokes.length / 2; i++) {
      out.push([[byStrokes[i]!], [byStrokes[byStrokes.length - 1 - i]!]])
    }
    return out
  }
  if (kind === 'teamHighLow' || kind === 'teamCombinedNet' || kind === 'teamFourBall') {
    if (byStrokes.length !== 4) return []
    const [low, second, third, high] = byStrokes as [PlayerId, PlayerId, PlayerId, PlayerId]
    return [[[low, high], [second, third]]]
  }
  // Quota and a bare card rank men; there is nothing to pair.
  return []
}

/**
 * Pair the round's foursomes off again, keeping every group id.
 *
 * The counterpart to the clearing above, and the reason clearing is safe: the
 * admin's way back is no longer "Generate pairings", which rebuilds the entire
 * week and takes the group ids with it.
 *
 * The split is the one four men use standing on the tee — sort on strokes, then
 * low with high against the two in the middle, and the same pairing off for
 * singles. It is not the optimiser's, and it cannot be: the optimiser weighs
 * who has already played whom across nine rounds, and re-running it is exactly
 * the thing being avoided. For one edited foursome the level split is the
 * honest answer and it is the answer the group would reach anyway.
 *
 * Only stale groups are rewritten, so a foursome the optimiser drew and nobody
 * touched keeps the contests it was given. A group that is not four men is left
 * alone and stays reported by `seatingProblems` — there is no sensible 2v2
 * among five, and quietly leaving one man out would be worse than saying so.
 */
export function redrawContests(
  t: Tournament,
  roundId: RoundId,
  strokesOf: (playerId: PlayerId) => number,
): Tournament {
  const match = t.matches.find((m) => m.roundId === roundId)
  if (!match || !needsContests(match.config.kind)) return t

  const touched = new Set<GroupId>()
  const built: Pairing[] = []

  /*
   * Ids derived from the group rather than randomly minted. A group id is
   * already unique in the document, so this cannot collide, and it makes a
   * re-pair idempotent — running it twice produces the same document, which is
   * what a screen that saves itself every 1.2 seconds wants.
   */
  const contest = (g: Group, index: number, a: PlayerId[], b: PlayerId[]): Pairing => ({
    id: `${g.id}~c${index}`,
    matchId: match.id,
    groupId: g.id,
    index,
    sides: [
      { id: `${g.id}~c${index}a`, playerIds: a },
      { id: `${g.id}~c${index}b`, playerIds: b },
    ],
    pinned: false,
  })

  for (const g of t.groups) {
    if (g.roundId !== roundId) continue
    if (!contestsAreStale(t, match, g)) continue
    if (g.playerIds.length !== 4) continue
    touched.add(g.id)
    levelSplit(g.playerIds, match.config.kind, strokesOf).forEach(([a, b], i) =>
      built.push(contest(g, i, a!, b!)),
    )
  }

  if (touched.size === 0) return t
  return { ...t, pairings: [...withoutContests(t, touched), ...built] }
}
